import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";

import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React from "react";
import { css } from "styled-components";

interface ManagerItem {
    fields: { role: string; name: string; email: string };
}
interface Fields {
    heading: string;
    managers: ManagerItem[];
}
type Props = WidgetProps & { fields: Fields };

export interface DivisionManagersStyles {
    wrapper?: InjectableCss;
    heading?: InjectableCss;
    tableScroll?: InjectableCss;
    table?: InjectableCss;
}

const EMAIL_BLUE = "#0b8fd8";
const ZEBRA_GREY = "#f2f2f2";

export const divisionManagersStyles: DivisionManagersStyles = {
    wrapper: {
        css: css`
            max-width: 812px;
            width: 100%;
            margin: 0 auto;
            padding: 0;
            margin-top: 104px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 40px;
                    `,
                    css`
                        margin-top: 64px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    heading: {
        css: css`
            font-size: 32px;
            line-height: 40px;
            font-weight: ${TYPOGRAPHY.weight.bold};
            color: ${COLORS.black};
            text-align: left;
            margin: 0 0 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 28px;
                        line-height: 36px;
                        margin-bottom: 16px;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    tableScroll: {
        css: css`
            overflow-x: auto;
        `,
    },
    table: {
        css: css`
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;

            /* header row stays for screen readers only */
            thead {
                position: absolute;
                width: 1px;
                height: 1px;
                overflow: hidden;
                clip: rect(0 0 0 0);
                white-space: nowrap;
            }

            tbody tr:nth-child(even) {
                background: ${ZEBRA_GREY};
            }

            td {
                text-align: left;
                vertical-align: middle;
                padding: 12px 16px;
                font-size: 14px;
                line-height: 16px;
                color: ${COLORS.black};
                word-break: break-word;
            }

            td.role {
                width: 50%;
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                font-size: 18px;
                line-height: 24px;
            }

            td.contact .name {
                font-weight: 400;
                margin-bottom: 4px;
            }

            td.contact a {
                display: block;
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                color: ${EMAIL_BLUE};
                text-decoration: none;
                 font-size: 18px;
                line-height: 24px;
                &:hover {
                    text-decoration: underline;
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    // xs: stack each row -> division, then name, then email
                    css`
                        table,
                        tbody,
                        tr,
                        td {
                            display: block;
                            width: 100%;
                        }
                        tr {
                            padding: 12px 16px;
                        }
                        td {
                            padding: 0;
                        }
                        td.role {
                            width: 100%;
                            line-height: 20px;
                            margin-bottom: 8px;
                        }
                        td.contact .name {
                            margin-bottom: 2px;
                        }
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};

const DivisionManagers = ({ fields }: Props) => {
    const styles = divisionManagersStyles;
    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography as="h2" {...styles.heading}>
                {fields.heading}
            </Typography>
            <StyledWrapper {...styles.tableScroll}>
                <StyledWrapper as="table" {...styles.table}>
                    <thead>
                        <tr>
                            <th scope="col">Division</th>
                            <th scope="col">Contact</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(fields.managers ?? []).map((m: any, i: any) => (
                            <tr key={i}>
                                <td className="role">{m.fields.role}</td>
                                <td className="contact">
                                    <div className="name">{m.fields.name}</div>
                                    {m.fields.email && <Link href={`mailto:${m.fields.email}`}>{m.fields.email}</Link>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: DivisionManagers,
    definition: {
        group: "Common",
        displayName: "CEN Division Managers Table",
        fieldDefinitions: [
            { name: "heading", displayName: "Heading", editorTemplate: "TextField", defaultValue: "Division Managers, Sales Managers, and Product Specialists", fieldType: "Translatable" },
            {
                name: "managers",
                displayName: "Managers",
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "Translatable",
                getDisplay: (item: any) => item.fields.name || item.fields.role,
                fieldDefinitions: [
                    { name: "role", displayName: "Division / role", editorTemplate: "TextField", defaultValue: "" },
                    { name: "name", displayName: "Name", editorTemplate: "TextField", defaultValue: "" },
                    { name: "email", displayName: "Email", editorTemplate: "TextField", defaultValue: "" },
                ],
            },
        ],
    },
};

export default widgetModule;